import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, XCircle, PlayCircle, ShieldCheck, Loader2 } from "lucide-react";

export const Route = createFileRoute("/rls-check")({
  head: () => ({
    meta: [
      { title: "RLS Self-Test — Coffman Cove Lodging" },
      { name: "robots", content: "noindex" },
      { name: "description", content: "Automated checks that verify row-level security rules on the gallery submission queue." },
    ],
  }),
  component: RlsCheck,
});

type Outcome = "pass" | "fail" | "skip";

type Result = {
  name: string;
  expected: string;
  actual: string;
  outcome: Outcome;
};

function RlsCheck() {
  const [session, setSession] = useState<{ email: string | null; userId: string | null } | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [results, setResults] = useState<Result[] | null>(null);
  const [running, setRunning] = useState(false);

  const loadIdentity = useCallback(async () => {
    const { data } = await supabase.auth.getUser();
    const user = data.user;
    setSession({ email: user?.email ?? null, userId: user?.id ?? null });
    if (!user) {
      setIsAdmin(false);
      return;
    }
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id);
    setIsAdmin(!!roles?.some((r) => r.role === "admin"));
  }, []);

  useEffect(() => {
    loadIdentity();
  }, [loadIdentity]);

  async function runChecks() {
    setRunning(true);
    setResults(null);
    const out: Result[] = [];

    const nonAdminReadShouldBeZero = isAdmin !== true;
    const expectRowsBlocked = nonAdminReadShouldBeZero;

    // 1. SELECT gallery_submissions
    {
      const { data, error } = await supabase
        .from("gallery_submissions")
        .select("id", { count: "exact", head: false })
        .limit(5);
      const rowCount = data?.length ?? 0;
      const actual = error
        ? `error: ${error.message}`
        : `${rowCount} row(s) returned`;
      const expected = expectRowsBlocked
        ? "0 rows (RLS blocks non-admins)"
        : "any number of rows (admin)";
      const pass = expectRowsBlocked ? !error && rowCount === 0 : !error;
      out.push({ name: "SELECT gallery_submissions", expected, actual, outcome: pass ? "pass" : "fail" });
    }

    // 2. SELECT submission_review_audit
    {
      const { data, error } = await supabase
        .from("submission_review_audit")
        .select("id")
        .limit(5);
      const rowCount = data?.length ?? 0;
      const actual = error ? `error: ${error.message}` : `${rowCount} row(s) returned`;
      const expected = expectRowsBlocked
        ? "0 rows (RLS blocks non-admins)"
        : "any number of rows (admin)";
      const pass = expectRowsBlocked ? !error && rowCount === 0 : !error;
      out.push({ name: "SELECT submission_review_audit", expected, actual, outcome: pass ? "pass" : "fail" });
    }

    // 3. INSERT gallery_submissions with pending status (should always succeed — anon/authenticated policy)
    let createdId: string | null = null;
    {
      const marker = `rls-check ${new Date().toISOString()}`;
      const { data, error } = await supabase
        .from("gallery_submissions")
        .insert({
          submitter_name: "RLS Self-Test",
          submitter_email: null,
          caption: marker,
          storage_path: `rls-check/${crypto.randomUUID()}.txt`,
          status: "pending",
        })
        .select("id")
        .single();
      createdId = data?.id ?? null;
      const actual = error ? `error: ${error.message}` : `inserted id ${createdId?.slice(0, 8)}…`;
      out.push({
        name: "INSERT pending submission",
        expected: "insert succeeds (open submission policy)",
        actual,
        outcome: error ? "fail" : "pass",
      });
    }

    // 4. INSERT gallery_submissions with approved status (should FAIL — policy check enforces pending)
    {
      const { error } = await supabase.from("gallery_submissions").insert({
        submitter_name: "RLS Self-Test",
        submitter_email: null,
        caption: "attempted approved insert",
        storage_path: `rls-check/${crypto.randomUUID()}.txt`,
        status: "approved",
      });
      const actual = error ? `blocked: ${error.message}` : "insert unexpectedly succeeded";
      out.push({
        name: "INSERT approved status (spoof attempt)",
        expected: "blocked by RLS check (status must be pending)",
        actual,
        outcome: error ? "pass" : "fail",
      });
    }

    // 5. UPDATE gallery_submissions status — non-admin must not change status
    if (createdId) {
      const { data, error } = await supabase
        .from("gallery_submissions")
        .update({ status: "approved" })
        .eq("id", createdId)
        .select("id");
      const rowsAffected = data?.length ?? 0;
      const blocked = !!error || rowsAffected === 0;
      const actual = error
        ? `blocked: ${error.message}`
        : `${rowsAffected} row(s) updated`;
      const expected = isAdmin
        ? "1 row updated (admin can approve)"
        : "0 rows updated (RLS blocks non-admin update)";
      const pass = isAdmin ? rowsAffected === 1 && !error : blocked;
      out.push({ name: "UPDATE gallery_submissions", expected, actual, outcome: pass ? "pass" : "fail" });
    } else {
      out.push({
        name: "UPDATE gallery_submissions",
        expected: "skipped",
        actual: "no test row available",
        outcome: "skip",
      });
    }

    // 6. DELETE gallery_submissions — non-admin must not delete
    if (createdId) {
      const { data, error } = await supabase
        .from("gallery_submissions")
        .delete()
        .eq("id", createdId)
        .select("id");
      const rowsAffected = data?.length ?? 0;
      const blocked = !!error || rowsAffected === 0;
      const actual = error
        ? `blocked: ${error.message}`
        : `${rowsAffected} row(s) deleted`;
      const expected = isAdmin
        ? "1 row deleted (admin can delete)"
        : "0 rows deleted (RLS blocks non-admin delete)";
      const pass = isAdmin ? rowsAffected === 1 && !error : blocked;
      out.push({ name: "DELETE gallery_submissions", expected, actual, outcome: pass ? "pass" : "fail" });
    } else {
      out.push({
        name: "DELETE gallery_submissions",
        expected: "skipped",
        actual: "no test row available",
        outcome: "skip",
      });
    }

    // 7. INSERT into submission_review_audit — nobody should be able to insert directly
    {
      const { error } = await supabase.from("submission_review_audit").insert({
        submission_id: createdId ?? crypto.randomUUID(),
        action: "approved",
        reviewer_id: session?.userId ?? null,
        reviewer_email: session?.email ?? "spoof@example.com",
      });
      const actual = error ? `blocked: ${error.message}` : "insert unexpectedly succeeded";
      out.push({
        name: "INSERT submission_review_audit (spoof attempt)",
        expected: "blocked (no INSERT policy — trigger only)",
        actual,
        outcome: error ? "pass" : "fail",
      });
    }

    setResults(out);
    setRunning(false);
  }

  const passCount = results?.filter((r) => r.outcome === "pass").length ?? 0;
  const failCount = results?.filter((r) => r.outcome === "fail").length ?? 0;
  const skipCount = results?.filter((r) => r.outcome === "skip").length ?? 0;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-12">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-7 w-7 text-emerald-600" />
        <h1 className="font-display text-3xl">RLS Self-Test</h1>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Runs a series of live queries as the current session to verify that row-level
        security correctly blocks non-admin access to the photo review queue and audit log.
      </p>

      <div className="mt-6 rounded-lg border border-border bg-[var(--color-parchment)] p-4 text-sm">
        <p>
          <strong>Signed in as:</strong>{" "}
          {session?.email ?? <span className="italic text-muted-foreground">anonymous</span>}
        </p>
        <p className="mt-1">
          <strong>Admin role:</strong>{" "}
          {isAdmin === null ? "checking…" : isAdmin ? "yes" : "no"}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          To fully verify the block, run this signed out AND signed in as a non-admin
          account. Non-admin runs must show all "pass" — any "fail" means RLS is leaking.
        </p>
      </div>

      <button
        onClick={runChecks}
        disabled={running || isAdmin === null}
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
      >
        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
        {running ? "Running…" : "Run checks"}
      </button>

      {results && (
        <div className="mt-8">
          <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 font-semibold">
              <CheckCircle2 className="h-3.5 w-3.5" /> {passCount} pass
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-2 py-0.5 font-semibold">
              <XCircle className="h-3.5 w-3.5" /> {failCount} fail
            </span>
            {skipCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2 py-0.5 font-semibold">
                {skipCount} skipped
              </span>
            )}
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Result</th>
                  <th className="text-left px-4 py-2 font-medium">Check</th>
                  <th className="text-left px-4 py-2 font-medium">Expected</th>
                  <th className="text-left px-4 py-2 font-medium">Actual</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {results.map((r, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 align-top">
                      {r.outcome === "pass" && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                          <CheckCircle2 className="h-4 w-4" /> pass
                        </span>
                      )}
                      {r.outcome === "fail" && (
                        <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                          <XCircle className="h-4 w-4" /> fail
                        </span>
                      )}
                      {r.outcome === "skip" && (
                        <span className="text-muted-foreground">skip</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top font-medium">{r.name}</td>
                    <td className="px-4 py-3 align-top text-muted-foreground">{r.expected}</td>
                    <td className="px-4 py-3 align-top break-all">{r.actual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
